<template>
  <div class="w-full flex flex-col gap-6 pb-10">
    <ProfileHeader
      :name="userName"
      :email="userData?.email"
      :avatar-url="userData?.avatarUrl"
      :is-active="userData?.isActive ?? true"
      :confirmed="userData?.confirmed ?? false"
      :uploading="isUploadingAvatar"
      @upload-avatar="handleAvatarUpload"
    />

    <Tabs v-model="activeTab" :tabs="tabList">
      <template #personal>
        <PersonalDataPanel :user-data="userData" :save="handleSavePersonalData" />
      </template>

      <template #security>
        <SecurityPanel :save="handleChangePassword" />
      </template>

      <template #notifications>
        <NotificationsPanel />
      </template>

      <template #organizations>
        <OrganizationsPanel />
      </template>
    </Tabs>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useToast } from 'vue-toastification'

import Tabs, { type TabItem } from '@/components/designSystem/Tabs/Tabs.vue'
import { useUserStore } from '@/pinia-store/useUsersStore'
import { useHeaderStore } from '@/pinia-store/useHeaderStore'
import type { UserDataResponse } from '@/Network/Types/Responses/UserResponse'

import ProfileHeader from './components/atoms/ProfileHeader.vue'
import PersonalDataPanel from './components/organisms/PersonalDataPanel.vue'
import SecurityPanel from './components/organisms/SecurityPanel.vue'
import NotificationsPanel from './components/organisms/NotificationsPanel.vue'
import OrganizationsPanel from './components/organisms/OrganizationsPanel.vue'

const userStore = useUserStore()
const headerStore = useHeaderStore()
const toast = useToast()

const { userData } = storeToRefs(userStore)

const activeTab = ref('personal')
const isUploadingAvatar = ref(false)

const tabList: TabItem[] = [
  { label: 'Dados pessoais', value: 'personal', testId: 'profile-tab-personal' },
  { label: 'Segurança', value: 'security', testId: 'profile-tab-security' },
  { label: 'Notificações', value: 'notifications', testId: 'profile-tab-notifications' },
  { label: 'Empresas', value: 'organizations', testId: 'profile-tab-organizations' }
]

const userName = computed(() => userData.value?.name || 'Usuário')

onMounted(async () => {
  headerStore.setTitle(
    'Perfil e Configurações',
    'Gerencie seus dados pessoais, segurança, notificações e empresas vinculadas.'
  )
  await userStore.getUserData()
})

const handleAvatarUpload = async (file: File) => {
  isUploadingAvatar.value = true
  try {
    await userStore.updateAvatar(file)
    toast.success('Foto de perfil alterada com sucesso!')
  } catch (error) {
    console.error('Erro ao enviar foto de perfil:', error)
    toast.error('Erro ao enviar foto de perfil. Tente novamente!')
  } finally {
    isUploadingAvatar.value = false
  }
}

const handleSavePersonalData = async (payload: Partial<UserDataResponse>) => {
  try {
    await userStore.updateUserData(payload)
    toast.success('Dados atualizados com sucesso!')
  } catch (error) {
    console.error('Erro ao atualizar os dados do usuário:', error)
    toast.error('Erro ao atualizar os dados. Tente novamente!')
    throw error // sinaliza falha ao painel (mantém o modo de edição)
  }
}

const handleChangePassword = async (payload: { password: string; repeatPassword: string }) => {
  try {
    await userStore.changePassword(payload.password, payload.repeatPassword)
    toast.success('Senha atualizada com sucesso!')
  } catch (error) {
    console.error('Erro ao atualizar a senha:', error)
    toast.error('Erro ao atualizar a senha. Tente novamente!')
    throw error // sinaliza falha ao painel (não limpa os campos)
  }
}
</script>
